import {Button} from '@wandb/weave/components/Button/Button';
import {get} from 'lodash';
import React, {useCallback} from 'react';

import {type ClassLabelNode} from '../../../../../../util/media';
import {type AllMaskControls, type MaskControl} from '../../../../../MediaCard';
import {getMaskControl as getDefaultedMaskControl} from '../../../../../PanelMediaBrowser/utils/maskControlUtils';
import {useMediaSegmentationMaskConfigDisplay} from '../../../hooks';
import {applyOverlayToggleUpdate} from '../../common/overlayToggleUpdate';
import {useMediaSettingsContext} from '../../contexts/MediaSettingsContextProvider';
import {MaskClasses, maskEntryHasCustomization} from './MaskClasses';

type MasksForMediaKeyProps = {
  mediaKey: string;
  maskSearchRegex: RegExp | null;
};

export function applyAllMasksToggleUpdate(
  config: AllMaskControls | undefined,
  mediaKey: string,
  relevantMasks: {[key: string]: ClassLabelNode}
): AllMaskControls {
  const allMasksConfigPath = ['toggles', mediaKey, 'all', 'all'];
  const nextDisabled = !getDefaultedMaskControl(
    config ?? {},
    allMasksConfigPath
  ).disabled;

  // Flip the global "all" control and every mask's parent "all" to the same
  // visibility. Each path reuses the shared single-path primitive, which also
  // clears that mask's per-class `disabled` overrides so they re-inherit. The
  // paths are disjoint, so folding one config through each update is equivalent
  // to a single batched mutation.
  const configPaths = [
    allMasksConfigPath,
    ...Object.keys(relevantMasks).map(maskKey => [
      'toggles',
      mediaKey,
      maskKey,
      'all',
    ]),
  ];

  return configPaths.reduce<AllMaskControls>(
    (acc, configPath) =>
      applyOverlayToggleUpdate(
        acc,
        configPath,
        getDefaultedMaskControl(acc, configPath),
        {disabled: nextDisabled},
        maskEntryHasCustomization
      ),
    config ?? {}
  );
}

export const MasksForMediaKey = ({
  mediaKey,
  maskSearchRegex,
}: MasksForMediaKeyProps) => {
  const {allMasks, updateConfig, settingsLevel, panelRef} =
    useMediaSettingsContext();
  const segmentationMaskConfig = useMediaSegmentationMaskConfigDisplay(
    settingsLevel,
    panelRef
  );

  const toggleAllMasks = (): void => {
    updateConfig({
      segmentationMaskConfig: applyAllMasksToggleUpdate(
        segmentationMaskConfig,
        mediaKey,
        allMasks[mediaKey]
      ),
    });
  };

  const relevantMasks = allMasks[mediaKey];

  const allMasksConfigPath = ['toggles', mediaKey, 'all', 'all'];
  const getMaskControl = useCallback(
    (configPath: Array<string>): MaskControl | undefined => {
      return get(segmentationMaskConfig ?? {}, configPath);
    },
    [segmentationMaskConfig]
  );
  const allMasksControl = getMaskControl(allMasksConfigPath);

  return (
    <div className="flex flex-col">
      <div className="ml-8 flex items-center">
        <Button
          data-test={`visibility-toggle-${mediaKey}-all`}
          variant="ghost"
          icon={!allMasksControl?.disabled ? 'visible' : 'not-visible'}
          onClick={toggleAllMasks}
        />
        <div className="ml-4 font-semibold">All Masks</div>
      </div>
      {Object.keys(relevantMasks).map(maskKey => (
        <MaskClasses
          getMaskControl={getMaskControl}
          maskSearchRegex={maskSearchRegex}
          maskKey={maskKey}
          mediaKey={mediaKey}
          key={`${mediaKey}-${maskKey}`}
        />
      ))}
    </div>
  );
};
